import { Box, Flex, Heading, RadioCards, Text, Spinner, Button } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useLazyStaffStoreAppDetailsQuery, useStaffStoresQuery } from "../../api";
import { updateGlobalAPIBaseURLs } from "../../api/axios/api-instances";
import { refreshAccessToken } from "../../api/helpers/refresh-token";
import { getAppKey } from "../../shared/lib/helpers";
import { convertServicesToEnvironment } from "../../shared/lib/environment";
import { useState } from "react";
import { useAppDispatch } from "../../store";
import { logout } from "../../store/slices/authSlice";
import { ExitIcon } from "@radix-ui/react-icons";

export function Stores() {
  const appKey = getAppKey();
  const { data, isLoading } = useStaffStoresQuery({ app_key: appKey });
  const [loadingStore, setLoadingStore] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const navigate = useNavigate();

  const onSelect = async (storeKey: string) => {
    setLoadingStore(storeKey);

    try {
      const appDetails = await getAppDetails({ store_key: storeKey, app_key: appKey }, false).unwrap();

      if (appDetails) {
        await refreshAccessToken();

        const services = convertServicesToEnvironment(appDetails.result.services);
        updateGlobalAPIBaseURLs(services);

        navigate(`/stores/${storeKey}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStore(null);
    }
  };

  return (
    <Flex height="100dvh" width="100%" justify="center" align="center">
      <Box className="space-y-4">
        <Heading align="center">Select store</Heading>

        <Button
          disabled={loading}
          className="!absolute !top-0 !right-8"
          variant="ghost"
          color="gray"
          onClick={() => {
            setLoading(true);
            dispatch(logout())
              .unwrap()
              .then(() => {
                setLoading(false);
                navigate("/login");
              });
          }}
        >
          <ExitIcon /> Logout
        </Button>

        <Box className="w-full">
          {isLoading ? (
            <Flex justify="center" align="center" className="w-full">
              <Spinner />
            </Flex>
          ) : data?.results.length === 0 ? (
            <Flex justify="center" align="center" className="w-full">
              <Text color="gray">No stores found</Text>
            </Flex>
          ) : (
            <RadioCards.Root onValueChange={onSelect} className="flex flex-wrap gap-4">
              {data?.results.map((store) => (
                <RadioCards.Item
                  value={store.key}
                  key={store.id}
                  className="min-w-[200px] basis-[calc(100%/3-1rem)]"
                >
                  {loadingStore === store.key && (
                    <Flex justify="center" align="center" className="absolute inset-0 bg-white/60">
                      <Spinner />
                    </Flex>
                  )}
                  <Flex direction="column" width="100%">
                    <Text weight="bold">{store.name}</Text>
                    <Text>{store.address}</Text>
                  </Flex>
                </RadioCards.Item>
              ))}
            </RadioCards.Root>
          )}
        </Box>
      </Box>
    </Flex>
  );
}
